export interface SalaryComparisonSeoRange {
  label: string;
  min: number;
  max: number;
  median: number;
  type:
    | 'job'
    | 'titleLocation'
    | 'companyLocation'
    | 'titleRoleLevel'
    | 'title'
    | 'roleLevel'
    | 'locationMarket';
}

export interface SalaryComparisonSeoRow {
  market: string;
  lowerBound: number;
  median: number;
  upperBound: number;
}

function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function toRow(range: SalaryComparisonSeoRange): SalaryComparisonSeoRow | null {
  if (
    !isPositiveFinite(range.min) ||
    !isPositiveFinite(range.max) ||
    !isPositiveFinite(range.median)
  ) {
    return null;
  }
  return {
    market: range.label,
    lowerBound: range.min,
    median: range.median,
    upperBound: range.max,
  };
}

function pickRange(
  ranges: SalaryComparisonSeoRange[],
  predicate: (range: SalaryComparisonSeoRange) => boolean,
): SalaryComparisonSeoRange | null {
  return ranges.find(predicate) ?? null;
}

export function buildSalaryComparisonSeoRows(
  ranges: SalaryComparisonSeoRange[],
): SalaryComparisonSeoRow[] {
  if (!Array.isArray(ranges) || ranges.length === 0) return [];

  const marketRange =
    pickRange(ranges, (range) => range.type === 'title') ??
    pickRange(ranges, (range) => range.type === 'titleRoleLevel') ??
    pickRange(ranges, (range) => range.type === 'roleLevel') ??
    pickRange(ranges, (range) => range.type === 'locationMarket');

  const locationRange =
    pickRange(ranges, (range) => range.type === 'titleLocation') ??
    pickRange(ranges, (range) => range.type === 'companyLocation') ??
    pickRange(ranges, (range) => range.type === 'locationMarket');

  const jobRange = pickRange(ranges, (range) => range.type === 'job');

  const rows = [marketRange, locationRange, jobRange]
    .filter((range): range is SalaryComparisonSeoRange => range !== null)
    .map(toRow)
    .filter((row): row is SalaryComparisonSeoRow => row !== null);

  const deduped: SalaryComparisonSeoRow[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const key = `${row.market}::${row.lowerBound}::${row.median}::${row.upperBound}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(row);
  }
  return deduped;
}
